import { apiClient as api } from "./httpClient";

// Configuração do provedor de IA da entidade (M1). Só o admin acessa (o backend valida via
// EntityOwnershipGuard + AiFeatureGuard). A chave é por tenant, na env — nunca trafega aqui.

export interface AiIntegrationOption {
  id: string;
  providerKey: string;
  displayName: string;
  defaultModel: string;
  models: string[];
  supportsSensitiveData: boolean;
  setupInstructions?: string;
  docsLink?: string;
}

export interface EntityIntegration {
  configured: boolean;
  inherited: boolean;
  integrationId?: string;
  model?: string;
  reasoningEffort?: string;
  enabled: boolean;
  /** Teto diário de respostas automáticas da entidade (RN-618); nulo = padrão do sistema. */
  maxRepliesPerDay?: number | null;
  /** Orçamento mensal de IA em US$ (D-HAR-9); nulo = sem orçamento. */
  monthlyBudgetUsd?: number | null;
  budgetAlertPercent?: number | null;
  budgetBlocks?: boolean;
  /** Gasto de IA da entidade no mês corrente (UTC), em US$. */
  monthSpentUsd?: number;
}

export interface AiConnectionTestResult {
  success: boolean;
  model: string;
  latencyMs: number;
  error?: string;
}

export interface SaveAiSelectionInput {
  entityId: string;
  integrationId: string;
  model?: string | null;
  reasoningEffort?: string | null;
  enabled: boolean;
  maxRepliesPerDay?: number | null;
  monthlyBudgetUsd?: number | null;
  budgetAlertPercent?: number | null;
  budgetBlocks?: boolean;
}

export interface PatientAssistantAgentOption {
  id: string;
  displayName: string;
  targetProfileId?: string | null;
  /** Para quantas pessoas o agente está liberado; zero deixa o assistente ligado e invisível. */
  assignedUsers: number;
}

/** `agentId` nulo = assistente do prontuário desligado; a liberação por pessoa fica no cadastro de agentes. */
export interface PatientAssistantSetting {
  agentId?: string | null;
  options: PatientAssistantAgentOption[];
  speechReply: boolean;
}

export interface AssistantAreaSetting {
  areaKey: string;
  readAgentId?: string | null;
  writeAgentId?: string | null;
  /** Resposta falada da área (RN-570). */
  speechReply: boolean;
}

export interface AssistantAgentOption {
  id: string;
  displayName?: string | null;
  /** Agente de alteração ligado no chat comum: a tela avisa para desligar. */
  showInChat: boolean;
  assignedUsers: number;
}

export interface AssistantAreasSetting {
  areas: AssistantAreaSetting[];
  agents: AssistantAgentOption[];
}

export const aiIntegrationService = {
  // Assistente do prontuário (item 206): agente próprio, liberado pessoa a pessoa em Agentes de IA
  getPatientAssistant: async (entityId: string): Promise<PatientAssistantSetting> => {
    const response = await api.get(`/ai-integration/${entityId}/patient-assistant`);
    return response.data;
  },
  // speechReply: modo conversa do assistente (D-OP-34, RN-570)
  savePatientAssistant: async (entityId: string, agentId: string | null, speechReply = false): Promise<void> => {
    await api.put(`/ai-integration/${entityId}/patient-assistant`, { agentId, speechReply });
  },

  // Agentes das áreas da página Assistentes (RN-565): consulta e alteração por área
  getAssistantAreas: async (entityId: string): Promise<AssistantAreasSetting> => {
    const response = await api.get(`/ai-integration/${entityId}/assistant-areas`);
    return response.data;
  },
  saveAssistantArea: async (
    entityId: string,
    areaKey: string,
    readAgentId: string | null,
    writeAgentId: string | null,
    speechReply: boolean
  ): Promise<void> => {
    await api.put(`/ai-integration/${entityId}/assistant-areas/${areaKey}`, { readAgentId, writeAgentId, speechReply });
  },

  getOptions: async (entityId: string): Promise<AiIntegrationOption[]> => {
    const response = await api.get(`/ai-integration/options/${entityId}`);
    return response.data;
  },
  getSelection: async (entityId: string): Promise<EntityIntegration> => {
    const response = await api.get(`/ai-integration/${entityId}`);
    return response.data;
  },
  save: async (input: SaveAiSelectionInput): Promise<void> => {
    await api.put("/ai-integration", input);
  },
  testConnection: async (
    entityId: string,
    integrationId: string,
    model?: string | null
  ): Promise<AiConnectionTestResult> => {
    const response = await api.post("/ai-integration/test", { entityId, integrationId, model });
    return response.data;
  },
};

// Rótulo amigável dos modelos conhecidos do Claude; fallback para o próprio id (outros provedores).
const MODEL_LABELS: Record<string, string> = {
  "claude-sonnet-5": "Claude Sonnet 5 (equilíbrio)",
  "claude-opus-4-8": "Claude Opus 4.8 (mais capaz)",
  "claude-haiku-4-5": "Claude Haiku 4.5 (rápido e econômico)",
  "claude-fable-5": "Claude Fable 5 (máxima capacidade)",
};

export const modelLabel = (id: string): string => MODEL_LABELS[id] || id;
